import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useApi,
  SKILL_LEVEL_LABELS,
  SKILL_LEVELS,
  SkillLevel,
  CURRENCY_SYMBOLS,
  UserRole,
} from '../api';
import { requestBotAlerts } from '../tg';
import { Icon } from '../Icon';
import { Photo } from '../Photo';
import { SkillBadge } from '../SkillBadge';
import { AdminCrownBadge, isAdminUser } from '../AdminCrownBadge';
import { useI18n } from '../i18n';
import { effectiveSkillLevel, hasSkillBadge } from '../lib/skill';
import { coverForPlayType, resolveGameCovers } from '../lib/play-type';
import { isEvalDone, markEvalDone } from '../lib/eval-done';
import { Modal } from '../Modal';
import { ReportUserModal } from './ReportUserModal';
import { EvaluatePlayersModal } from './EvaluatePlayersModal';
import { InvitePlayerModal } from './InvitePlayerModal';
import { EditGameModal } from './EditGameModal';
import { confirmDialog } from '../lib/confirm';
import { formatGameDateTime } from '../lib/datetime';
import { isGameLive } from '../lib/game-status';
import { buildGameShareText, gameDeepLink, shareGameToTelegram } from '../lib/share-game';
import './GameDetail.css';

/** Rating form unlocks only when status is FINISHED (host or auto +5h). */
function gameReadyForEval(game: { status: string }): boolean {
  return game.status === 'FINISHED';
}

function formatMoney(minor: number, currency: string): string {
  return `${CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] ?? currency}${(minor / 100).toFixed(2)}`;
}

interface PlayerRowProps {
  userId: string;
  photoUrl: string | null;
  firstName: string;
  lastName: string | null | undefined;
  /** Self-declared level from the welcome flow. */
  skillLevel: SkillLevel | null;
  /** Weighted (peer-corrected) level, computed by the backend. */
  evaluatedSkillLevel: SkillLevel | null;
  showWheelchairBadge?: boolean;
  role?: UserRole | null;
  isHost: boolean;
  isYou: boolean;
  roleLabel: string;
  /** Roster redacted for outsiders — show Incognito, no profile/menu. */
  identityHidden?: boolean;
  onOpenProfile: () => void;
  onReport: () => void;
  menuOpen: boolean;
  onToggleMenu: () => void;
}

/**
 * One row of the Players list. Layout matches the reference:
 *   [Photo]  [Name + role label]            [⋯ menu]
 * Report is hidden inside the overflow menu instead of a wide button in every
 * row — that was the "huge button next to every player" you saw on the old
 * build (a padded `.btn-ghost` rendering as a pill, not the icon-only button
 * it was supposed to be).
 */
function PlayerRow({
  userId,
  photoUrl,
  firstName,
  lastName,
  skillLevel,
  evaluatedSkillLevel,
  showWheelchairBadge,
  role,
  isHost,
  isYou,
  roleLabel,
  identityHidden,
  onOpenProfile,
  onReport,
  menuOpen,
  onToggleMenu,
}: PlayerRowProps) {
  const { t } = useI18n();
  if (identityHidden) {
    return (
      <li className="detailPlayer" data-user-id={userId}>
        <div className="detailPlayer-main detailPlayer-hold">
          <Photo src={null} name={t('game.incognito')} size={44} variant="rounded" />
          <span className="detailPlayer-body">
            <span className="detailPlayer-name">
              <span className="detailPlayer-nameText">{t('game.incognito')}</span>
            </span>
            <span className="detailPlayer-sub">{t('game.playersHiddenHint')}</span>
          </span>
        </div>
      </li>
    );
  }
  const fullName = lastName ? `${firstName} ${lastName}` : firstName;
  const showMenu = !isYou;
  // Prefer the peer-corrected (weighted) level. Falls back to self-declared
  // when the backend hasn't computed a value yet. Wheelchair badge can show
  // even without a numeric level.
  const level = effectiveSkillLevel({ skillLevel, evaluatedSkillLevel });
  const showBadge = hasSkillBadge({ skillLevel, evaluatedSkillLevel, showWheelchairBadge });
  // The "HOST" pill already announces the host's role. Showing the
  // "Organizer" subtitle next to it is just noise — the pill says the
  // same thing twice. For host rows we hide the role label and put the
  // skill badge there instead, so the second line is always meaningful.
  const showRoleLabel = !isHost;
  const showAdmin = isAdminUser({ role });
  return (
    <li className="detailPlayer" data-user-id={userId}>
      <button
        type="button"
        className="detailPlayer-main"
        onClick={onOpenProfile}
        data-analytics-label="game-player-open-profile"
      >
        <Photo
          src={photoUrl}
          name={fullName}
          size={44}
          variant="rounded"
          topLeftBadge={showAdmin ? <AdminCrownBadge title={t('profile.status.admin')} /> : null}
          bottomRightBadge={
            showBadge ? (
              <SkillBadge
                level={level}
                wheelchair={!!showWheelchairBadge}
                size="sm"
                className="skillBadge-on-photo"
              />
            ) : null
          }
        />
        <span className="detailPlayer-body">
          <span className="detailPlayer-name">
            <span className="detailPlayer-nameText">{fullName}</span>
            {isYou && (
              <span className="detailPlayer-tag detailPlayer-tag-you">
                {t('gameDetail.tagYou')}
              </span>
            )}
          </span>
          <span className="detailPlayer-sub">
            {isHost && (
              <span className="detailPlayer-tag detailPlayer-tag-host">
                {t('gameDetail.tagHost')}
              </span>
            )}
            {showRoleLabel && <span>{roleLabel}</span>}
          </span>
        </span>
      </button>
      {showMenu && (
        <div className="detailPlayer-menu">
          <button
            className="btn-icon detailPlayer-menuBtn"
            onClick={onToggleMenu}
            aria-label={t('gameDetail.actionsFor', { name: fullName })}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-analytics-label="game-player-menu"
          >
            {/* Inline Unicode "⋯" (U+22EF) instead of an icon-font glyph.
                The Hugeicons webfont occasionally doesn't render this
                particular glyph depending on cache state, leaving the
                button blank. Unicode text always renders. */}
            <span className="detailPlayer-menuDots" aria-hidden="true">
              ⋯
            </span>
          </button>
          {menuOpen && (
            <div className="detailPlayer-menuPop" role="menu">
              <button
                className="detailPlayer-menuItem"
                onClick={() => {
                  onToggleMenu();
                  onOpenProfile();
                }}
                role="menuitem"
                data-analytics-label="game-view-profile"
              >
                <Icon name="user-account" size={14} /> {t('userProfile.view')}
              </button>
              <button
                className="detailPlayer-menuItem"
                onClick={() => {
                  onToggleMenu();
                  onReport();
                }}
                role="menuitem"
                data-analytics-label="game-report-player"
              >
                <Icon name="flag-01" size={14} /> {t('gameDetail.reportAction')}
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function GameDetailPage() {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const meQ = useQuery(['me'], () => api.me());
  const gameQ = useQuery(['game', id], () => api.getGame(id!), { enabled: !!id });
  const cityQ = useQuery(['default-city'], () => api.defaultCity(), {
    staleTime: 60 * 60_000,
  });
  const [shareError, setShareError] = useState<string | null>(null);

  // Pending join-request for closed lobbies
  const myJoinRequest = useMemo(() => {
    if (!gameQ.data || !meQ.data) return null;
    return gameQ.data.joinRequests?.find((r) => r.userId === meQ.data!.id && r.status === 'PENDING') ?? null;
  }, [gameQ.data, meQ.data]);

  const [pendingJoinBlockedWarn, setPendingJoinBlockedWarn] = useState<string[] | null>(null);

  const refreshGameLists = () => {
    // Home / Games / Calendar all key off `['games', …]` — must drop those
    // after cancel/leave/join or the cancelled game stays visible from cache.
    qc.invalidateQueries(['game', id]);
    qc.invalidateQueries(['games']);
    qc.invalidateQueries(['game-activity', id]);
  };

  const joinMut = useMutation(() => api.joinGame(id!), {
    onSuccess: () => {
      refreshGameLists();
      void requestBotAlerts();
    },
  });
  const leaveMut = useMutation(() => api.leaveGame(id!), {
    onSuccess: () => refreshGameLists(),
  });
  const cancelMut = useMutation(() => api.cancelGame(id!), {
    onSuccess: () => refreshGameLists(),
  });
  const finishMut = useMutation(() => api.finishGame(id!), {
    onSuccess: () => {
      // Invalidate both the per-game cache and the home/feed list cache
      // so the game disappears from "Upcoming games" immediately.
      refreshGameLists();
      qc.invalidateQueries(['evaluations', 'pending']);
      // Host is always a participant — open the rating modal immediately.
      setShowEvaluate(true);
    },
  });

  const decideJoinMut = useMutation(
    ({ requestId, accept }: { requestId: string; accept: boolean }) =>
      api.decideJoinRequest(id!, requestId, accept),
    {
      onSuccess: () => {
        qc.invalidateQueries(['game', id]);
        qc.invalidateQueries(['game-activity', id]);
      },
    },
  );

  const waitlistQ = useQuery(
    ['game-waitlist', id],
    () => api.getWaitlistMe(id!),
    {
      enabled:
        !!id &&
        !!meQ.data &&
        !!gameQ.data &&
        !gameQ.data.participants.some((p) => p.userId === meQ.data!.id) &&
        (gameQ.data.status === 'FULL' ||
          gameQ.data.participantsCount + (gameQ.data.reservedCount ?? 0) >= gameQ.data.spotsTotal),
    },
  );

  const waitlistMut = useMutation(
    (subscribe: boolean) =>
      subscribe ? api.joinWaitlist(id!) : api.leaveWaitlist(id!),
    {
      onSuccess: (_data, subscribe) => {
        qc.invalidateQueries(['game-waitlist', id]);
        qc.invalidateQueries(['game-waitlist-admin', id]);
        setWaitlistToast(
          subscribe ? t('game.waitlistToast') : t('game.waitlistToastOff'),
        );
        window.setTimeout(() => setWaitlistToast(null), 4200);
      },
    },
  );

  const isAdmin = isAdminUser(meQ.data ?? null);
  const activityQ = useQuery(
    ['game-activity', id],
    () => api.getGameActivity(id!),
    { enabled: !!id && isAdmin },
  );
  const reservationsQ = useQuery(
    ['game-reservations', id],
    () => api.getGameReservations(id!),
    { enabled: !!id && isAdmin },
  );
  const waitlistAdminQ = useQuery(
    ['game-waitlist-admin', id],
    () => api.getGameWaitlist(id!),
    { enabled: !!id && isAdmin },
  );
  const [reserveNote, setReserveNote] = useState('');
  const addReserveMut = useMutation(
    () => api.addGameReservation(id!, reserveNote.trim() || undefined),
    {
      onSuccess: () => {
        setReserveNote('');
        qc.invalidateQueries(['game', id]);
        qc.invalidateQueries(['games']);
        qc.invalidateQueries(['game-reservations', id]);
      },
    },
  );
  const removeReserveMut = useMutation(
    (reservationId: string) => api.removeGameReservation(id!, reservationId),
    {
      onSuccess: () => {
        qc.invalidateQueries(['game', id]);
        qc.invalidateQueries(['games']);
        qc.invalidateQueries(['game-reservations', id]);
      },
    },
  );

  const [reportTarget, setReportTarget] = useState<{ id: string; name: string } | null>(null);
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [showEvaluate, setShowEvaluate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [showEditGame, setShowEditGame] = useState(false);
  const [editBanner, setEditBanner] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'players' | 'info'>('players');
  const [waitlistToast, setWaitlistToast] = useState<string | null>(null);
  const [heroCoverIdx, setHeroCoverIdx] = useState(0);

  // Check for blacklisted players in the game
  const participantIds = useMemo(() => {
    if (!gameQ.data) return [] as string[];
    const ids = new Set<string>();
    if (gameQ.data.host?.id) ids.add(gameQ.data.host.id);
    gameQ.data.participants.forEach((p) => ids.add(p.userId));
    ids.delete(meQ.data?.id ?? '');
    return Array.from(ids);
  }, [gameQ.data, meQ.data]);

  const intersectQ = useQuery(
    ['blacklist', 'intersect', id, participantIds.join(',')],
    () => api.intersectBlacklist(participantIds),
    { enabled: !!gameQ.data && !!meQ.data && participantIds.length > 0 },
  );

  const blockedInThisGame = useMemo(() => {
    if (!intersectQ.data || !gameQ.data) return [] as { id: string; name: string }[];
    return intersectQ.data.blocked
      .map((bid) => {
        if (bid === gameQ.data!.host.id) {
          return { id: bid, name: `${gameQ.data!.host.firstName}` };
        }
        const p = gameQ.data!.participants.find((p) => p.userId === bid);
        return p ? { id: bid, name: `${p.user.firstName}` } : null;
      })
      .filter((x): x is { id: string; name: string } => !!x);
  }, [intersectQ.data, gameQ.data]);

  // Auto-show the warning dialog on first load if there are blocked players
  useEffect(() => {
    if (pendingJoinBlockedWarn) return;
    if (blockedInThisGame.length === 0) return;
    setPendingJoinBlockedWarn(blockedInThisGame.map((b) => b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedInThisGame.length]);

  // Auto-offer post-game ratings once the game is finished or past endAt.
  useEffect(() => {
    const g = gameQ.data;
    if (!g || !meQ.data) return;
    if (!gameReadyForEval(g)) return;
    const joined = g.participants.some((p) => p.userId === meQ.data!.id);
    if (!joined) return;
    if (isEvalDone(g.id)) return;
    if (g.participants.length < 2) return;
    setShowEvaluate(true);
  }, [gameQ.data, meQ.data]);

  if (gameQ.isLoading) return <div className="empty">{t('common.loading')}</div>;
  if (gameQ.isError) return <div className="error">{(gameQ.error as Error).message}</div>;
  if (!gameQ.data) return null;

  const g = gameQ.data;
  const myId = meQ.data?.id;
  const isHost = myId && g.host.id === myId;
  const isJoined = !!g.participants.find((p) => p.userId === myId);
  const isFull = g.participantsCount + (g.reservedCount ?? 0) >= g.spotsTotal;
  const occupied = g.participantsCount + (g.reservedCount ?? 0);
  const spotsLeft = Math.max(0, g.spotsTotal - occupied);
  const isClosed = g.status === 'CANCELLED' || g.status === 'FINISHED';
  const canEvaluate = gameReadyForEval(g);
  const heroCovers = resolveGameCovers(g);
  const heroSrc = heroCovers[Math.min(heroCoverIdx, heroCovers.length - 1)] ?? coverForPlayType(g.playType);

  const handleJoinClick = () => {
    if (blockedInThisGame.length > 0) {
      setPendingJoinBlockedWarn(blockedInThisGame.map((b) => b.name));
      return;
    }
    joinMut.mutate();
  };

  return (
    <>
      {/* Hero card — venue image with gradient overlay, big venue name,
          green capacity pill anchored top-right, date + location at the
          bottom. Matches the `GameHeroCard` Flutter reference. */}
      <div className="detailHero">
        <img
          className="detailHero-img"
          src={heroSrc}
          alt=""
          onError={(e) => {
            const el = e.currentTarget as HTMLImageElement;
            // Fall back to stock play-type art if a custom URL 404s.
            if (el.src !== coverForPlayType(g.playType) && !el.dataset.fallback) {
              el.dataset.fallback = '1';
              el.src = coverForPlayType(g.playType);
              return;
            }
            el.classList.add('detailHero-img-fallback');
          }}
        />
        <div className="detailHero-overlay" aria-hidden />
        {heroCovers.length > 1 && (
          <div className="detailHero-dots" role="tablist" aria-label={t('gameDetail.cover')}>
            {heroCovers.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === heroCoverIdx}
                className={`detailHero-dot${i === heroCoverIdx ? ' isActive' : ''}`}
                onClick={() => setHeroCoverIdx(i)}
              />
            ))}
          </div>
        )}
        <div className="detailHero-body">
          <div className="detailHero-top">
            <h2 className="detailHero-title">{g.venue.name}</h2>
            <span className="detailHero-pill">
              {occupied}/{g.spotsTotal}
            </span>
          </div>
          <div className="detailHero-bottom">
            <div className="detailHero-meta">
              <span>
                <Icon name="calendar-01" size={14} /> {formatGameDateTime(g.startAt, { locale: lang })}
              </span>
              <span>
                <Icon name="map-pin" size={14} /> {g.venue.address}
                {g.addressHint ? ` · ${g.addressHint}` : ''}
              </span>
            </div>
            {!isJoined && isFull && !isClosed && (
              <button
                type="button"
                className={`detailHero-notify${waitlistQ.data?.onWaitlist ? ' isOn' : ' isPulse'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  waitlistMut.mutate(!waitlistQ.data?.onWaitlist);
                }}
                disabled={waitlistMut.isLoading || waitlistQ.isLoading}
                aria-pressed={!!waitlistQ.data?.onWaitlist}
                data-analytics-label={
                  waitlistQ.data?.onWaitlist ? 'game-waitlist-stop' : 'game-waitlist-join'
                }
              >
                <Icon name="bell-dot" size={15} />
                <span>
                  {waitlistQ.data?.onWaitlist
                    ? t('game.waitlistSubscribed')
                    : t('game.waitlistNotify')}
                </span>
              </button>
            )}
          </div>
        </div>
        {waitlistToast && (
          <div className="detailHero-toast" role="status">
            {waitlistToast}
          </div>
        )}
      </div>

      {isAdmin && isFull && (
        <div className="detailNotifyCount" role="status">
          <Icon name="bell-dot" size={14} />
          <span>{t('gameDetail.notifyCount', { n: waitlistAdminQ.data?.count ?? 0 })}</span>
          {(waitlistAdminQ.data?.items.length ?? 0) > 0 && (
            <ul className="detailNotifyCount-list">
              {waitlistAdminQ.data!.items.map((row) => {
                const name = row.user.lastName
                  ? `${row.user.firstName} ${row.user.lastName}`
                  : row.user.firstName;
                const label = row.user.username ? `${name} (@${row.user.username})` : name;
                return <li key={row.id}>{label}</li>;
              })}
            </ul>
          )}
        </div>
      )}

      {/* Tab strip — Players / Info */}
      <div className="detailTabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'players'}
          className={`detailTab${activeTab === 'players' ? ' isActive' : ''}`}
          onClick={() => setActiveTab('players')}
          data-analytics-label="game-tab-players"
        >
          <Icon name="user-group" size={16} />
          <span>{t('gameDetail.tabPlayers')}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'info'}
          className={`detailTab${activeTab === 'info' ? ' isActive' : ''}`}
          onClick={() => setActiveTab('info')}
          data-analytics-label="game-tab-info"
        >
          <Icon name="information-circle" size={16} />
          <span>{t('gameDetail.tabInfo')}</span>
        </button>
      </div>

      {/* Closed-lobby pending-request banner */}
      {g.isClosed && myJoinRequest && (
        <div className="requestBanner">
          <Icon name="clock-01" size={14} />
          <span>{t('game.requestPending')}</span>
        </div>
      )}

      {g.isClosed && !isJoined && !myJoinRequest && g.status === 'OPEN' && (
        <div className="requestBanner">
          <Icon name="information-circle" size={14} />
          <span>{t('game.closedHint')}</span>
        </div>
      )}

      {g.playersHidden && (
        <div className="requestBanner">
          <Icon name="view-off" size={14} />
          <span>{t('game.playersHiddenBanner')}</span>
        </div>
      )}

      {/* Players tab body */}
      {activeTab === 'players' && (
        <div className="detailCard detailCard-players" role="tabpanel">
          <div className="detailPlayers-header">
            <h3>{t('gameDetail.confirmedPlayers', { n: g.participantsCount })}</h3>
            <span className="detailPlayers-count">
              {occupied}/{g.spotsTotal}
            </span>
          </div>

          {/* Confirmed players */}
          <ul className="detailPlayerList">
            <PlayerRow
              key={`host:${g.host.id}`}
              userId={g.host.id}
              photoUrl={g.host.photoUrl}
              firstName={g.host.firstName}
              lastName={g.host.lastName}
              skillLevel={g.host.skillLevel}
              evaluatedSkillLevel={g.host.evaluatedSkillLevel}
              showWheelchairBadge={g.host.showWheelchairBadge}
              role={g.host.role}
              isHost
              isYou={myId === g.host.id}
              roleLabel={t('gameDetail.roleOrganizer')}
              onOpenProfile={() =>
                navigate(myId === g.host.id ? '/profile' : `/users/${g.host.id}`)
              }
              onReport={() => setReportTarget({ id: g.host.id, name: g.host.firstName })}
              menuOpen={openMenuFor === g.host.id}
              onToggleMenu={() =>
                setOpenMenuFor(openMenuFor === g.host.id ? null : g.host.id)
              }
            />
            {g.participants
              .filter((p) => p.userId !== g.host.id)
              .map((p) => {
                const hidden = !!(p.identityHidden || p.user?.identityHidden);
                return (
                  <PlayerRow
                    key={p.id}
                    userId={p.userId}
                    photoUrl={hidden ? null : p.user.photoUrl}
                    firstName={hidden ? '' : p.user.firstName}
                    lastName={hidden ? null : p.user.lastName}
                    skillLevel={hidden ? null : p.user.skillLevel}
                    evaluatedSkillLevel={hidden ? null : p.user.evaluatedSkillLevel}
                    showWheelchairBadge={hidden ? false : p.user.showWheelchairBadge}
                    role={hidden ? undefined : p.user.role}
                    isHost={false}
                    isYou={!hidden && myId === p.userId}
                    roleLabel={t('gameDetail.rolePlayer')}
                    identityHidden={hidden}
                    onOpenProfile={() => {
                      if (hidden) return;
                      navigate(myId === p.userId ? '/profile' : `/users/${p.userId}`);
                    }}
                    onReport={() => {
                      if (hidden) return;
                      setReportTarget({ id: p.userId, name: p.user.firstName });
                    }}
                    menuOpen={!hidden && openMenuFor === p.userId}
                    onToggleMenu={() => {
                      if (hidden) return;
                      setOpenMenuFor(openMenuFor === p.userId ? null : p.userId);
                    }}
                  />
                );
              })}
            {g.participants.length <= 1 && (
              <li className="detailPlayer-empty">{t('gameDetail.noPlayers')}</li>
            )}
          </ul>

          {isHost && g.isClosed && (g.joinRequests?.length ?? 0) > 0 && (
            <div className="joinRequestList" style={{ marginTop: 16 }}>
              <div className="detailPlayers-subheader">
                <span>{t('game.joinRequests', { n: g.joinRequests!.length })}</span>
              </div>
              <ul className="detailPlayerList">
                {g.joinRequests!.map((r) => {
                  const u = r.user;
                  const name = u
                    ? `${u.firstName}${u.lastName ? ` ${u.lastName}` : ''}`
                    : r.userId;
                  return (
                    <li key={r.id} className="detailPlayer" style={{ flexWrap: 'wrap', gap: 8 }}>
                      <button
                        type="button"
                        className="detailPlayer-main"
                        style={{ flex: 1, minWidth: 0 }}
                        onClick={() => u?.id && navigate(`/users/${u.id}`)}
                        data-analytics-label="game-join-request-open-profile"
                      >
                        <Photo
                          src={u?.photoUrl ?? null}
                          name={name}
                          size={36}
                          topLeftBadge={
                            isAdminUser(u) ? (
                              <AdminCrownBadge title={t('profile.status.admin')} size="sm" />
                            ) : null
                          }
                          bottomRightBadge={
                            u && hasSkillBadge(u) ? (
                              <SkillBadge
                                level={(u.evaluatedSkillLevel ?? u.skillLevel) as SkillLevel}
                                wheelchair={!!u.showWheelchairBadge}
                                size="sm"
                                className="skillBadge-on-photo"
                              />
                            ) : null
                          }
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{name}</div>
                          {u?.username && (
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>@{u.username}</div>
                          )}
                        </div>
                      </button>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={decideJoinMut.isLoading}
                          onClick={() => decideJoinMut.mutate({ requestId: r.id, accept: true })}
                          data-analytics-label="join-request-approve"
                        >
                          <Icon name="checkmark-square-01" size={14} />
                          {t('game.approveRequest')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          disabled={decideJoinMut.isLoading}
                          onClick={() => decideJoinMut.mutate({ requestId: r.id, accept: false })}
                          data-analytics-label="join-request-reject"
                        >
                          <Icon name="cancel-01" size={14} />
                          {t('game.rejectRequest')}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Incognito holds — a seat taken for someone who cannot join yet. */}
          {(g.reservations?.length ?? 0) > 0 && (
            <>
              <div className="detailPlayers-subheader">
                <span>{t('game.reservedSpots', { n: g.reservations!.length })}</span>
              </div>
              <ul className="detailPlayerList">
                {g.reservations!.map((r) => {
                  const note = isAdmin
                    ? reservationsQ.data?.items.find((item) => item.id === r.id)?.note
                    : null;
                  return (
                    <li key={r.id} className="detailPlayer">
                      <div className="detailPlayer-main detailPlayer-hold">
                        <Photo src={null} name={t('game.incognito')} size={44} variant="rounded" />
                        <span className="detailPlayer-body">
                          <span className="detailPlayer-name">
                            <span className="detailPlayer-nameText">{t('game.incognito')}</span>
                          </span>
                          <span className="detailPlayer-sub">
                            {note || t('game.incognitoHint')}
                          </span>
                        </span>
                      </div>
                      {isAdmin && !isClosed && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          disabled={removeReserveMut.isLoading}
                          onClick={() => removeReserveMut.mutate(r.id)}
                          data-analytics-label="game-reserve-remove"
                        >
                          {t('game.reserveRemove')}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {isAdmin && !isClosed && (
            <div className="reserveBox">
              <p className="reserveBox-hint">{t('game.reserveHint')}</p>
              <input
                className="reserveBox-note"
                value={reserveNote}
                maxLength={80}
                placeholder={t('game.reserveNotePlaceholder')}
                onChange={(e) => setReserveNote(e.target.value)}
                aria-label={t('game.reserveNote')}
              />
              <button
                type="button"
                className="btn btn-sm"
                disabled={addReserveMut.isLoading || isFull}
                onClick={() => addReserveMut.mutate()}
                data-analytics-label="game-reserve-add"
              >
                <Icon name="user-account" size={14} /> {t('game.reserve')}
              </button>
              {addReserveMut.isError && (
                <div className="error">{(addReserveMut.error as Error).message}</div>
              )}
            </div>
          )}

          {/* Spots left as filled placeholder avatars */}
          {spotsLeft > 0 && (
            <>
              <div className="detailPlayers-subheader">
                <span>
                  {t('game.spotsLeft', { n: spotsLeft })}
                </span>
              </div>
              <div
                className="detailPlayers-spots"
                aria-label={t('game.spotsLeft', { n: spotsLeft })}
              >
                {Array.from({ length: spotsLeft }).map((_, i) => (
                  <span key={i} className="detailPlayers-spotSlot">
                    <Icon name="user-account" size={18} />
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Info tab body — venue, time, tags, notes, pricing */}
      {activeTab === 'info' && (
        <div className="detailCard detailCard-info" role="tabpanel">
          <div className="detailInfo-row">
            <Icon name="calendar-01" className="icon-inline" />
            <span>
              <strong>{t('gameDetail.startsAt')}:</strong>{' '}
              {formatGameDateTime(g.startAt, { locale: lang })}
            </span>
          </div>
          <div className="detailInfo-row">
            <Icon name="map-pin" className="icon-inline" />
            <span>
              <strong>{t('gameDetail.address')}:</strong> {g.venue.address}
              {g.addressHint ? ` · ${g.addressHint}` : ''}
            </span>
          </div>
          {g.isPaid && (
            <div className="detailInfo-row">
              <Icon name="dollar-01" className="icon-inline" />
              <span>
                <strong>{t('gameDetail.pricePerPlayer')}:</strong>{' '}
                {formatMoney(g.perPlayerCost, g.currency)}
              </span>
            </div>
          )}
          <div className="detailInfo-tags">
            <SkillBadge level={g.skillLevel} size="sm" />
            <span className="tag info">
              <Icon name={g.playType === 'INDOOR' ? 'building-01' : g.playType === 'BEACH' ? 'tennis-ball' : 'globe'} size={10} className="icon-inline" style={{ marginRight: 4 }} />
              {t(`game.playType.${g.playType.toLowerCase()}`)}
            </span>
            {g.isPaid && <span className="tag warning">{t('game.paid')}</span>}
            {g.isClosed && (
              <span className="tag" style={{ background: 'var(--surface-3)' }}>
                <Icon name="lock" size={10} /> {t('game.closed')}
              </span>
            )}
            {isGameLive(g) && <span className="tag warn">{t('status.going')}</span>}
            {g.status === 'CANCELLED' && <span className="tag warn">{t('game.cancel')}</span>}
            {g.status === 'FULL' && !isGameLive(g) && (
              <span className="tag warn">{t('game.spotsFull')}</span>
            )}
            {g.status === 'FINISHED' && <span className="tag">{t('game.finish')}</span>}
          </div>
          {g.notes && (
            <div className="detailInfo-notes">
              <Icon name="note-01" className="icon-inline" /> {g.notes}
            </div>
          )}

          {isAdmin && (
            <div className="detailActivity">
              <h3 className="detailActivity-title">
                <Icon name="clock-01" size={14} />
                {t('gameDetail.activityTitle')}
              </h3>
              {activityQ.isLoading && (
                <p className="detailActivity-empty">{t('common.loading')}</p>
              )}
              {activityQ.isError && (
                <p className="detailActivity-empty">{(activityQ.error as Error).message}</p>
              )}
              {activityQ.data && activityQ.data.items.length === 0 && (
                <p className="detailActivity-empty">{t('gameDetail.activityEmpty')}</p>
              )}
              {activityQ.data && activityQ.data.items.length > 0 && (
                <ul className="detailActivity-list">
                  {activityQ.data.items.map((row) => {
                    const name = row.user.lastName
                      ? `${row.user.firstName} ${row.user.lastName}`
                      : row.user.firstName;
                    const nick = row.user.username ? `@${row.user.username}` : name;
                    const when = formatGameDateTime(row.createdAt, { locale: lang });
                    return (
                      <li key={row.id} className="detailActivity-item">
                        <span className={`detailActivity-kind detailActivity-kind-${row.kind.toLowerCase()}`}>
                          {row.kind === 'JOINED'
                            ? t('gameDetail.activityJoined', { name: nick, when })
                            : t('gameDetail.activityLeft', { name: nick, when })}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action area */}
      {!isClosed && (
        <div className="detailActions">
          <button
            type="button"
            className="btn btn-sm btn-ghost detailActions-secondary"
            onClick={() => {
              setShareError(null);
              const bot = cityQ.data?.botUsername;
              if (!bot) {
                setShareError(t('game.shareUnavailable'));
                return;
              }
              const when = formatGameDateTime(g.startAt, { locale: lang });
              const skillNum = SKILL_LEVELS.indexOf(g.skillLevel) + 1;
              const left = spotsLeft;
              const spotsLine =
                left > 0
                  ? t('game.shareSpots', {
                      filled: occupied,
                      total: g.spotsTotal,
                      left,
                    })
                  : t('game.shareSpotsFull', {
                      filled: occupied,
                      total: g.spotsTotal,
                    });
              const priceLabel = g.isPaid
                ? t('game.perPlayer', {
                    amount: formatMoney(g.perPlayerCost ?? 0, g.currency),
                  })
                : t('game.shareFree');
              const link = gameDeepLink(bot, g.id);
              const text = buildGameShareText({
                headline: t('game.shareHeadline'),
                cta: t('game.shareCta'),
                link,
                venueName: g.venue.name,
                address: g.venue.address,
                when,
                playTypeLabel: t(`game.playType.${g.playType.toLowerCase()}`),
                skillLabel: skillNum > 0 ? t('game.shareSkill', { n: skillNum }) : null,
                closed: g.isClosed,
                spotsLine,
                priceLabel,
              });
              const ok = shareGameToTelegram({
                botUsername: bot,
                gameId: g.id,
                text,
              });
              if (!ok) setShareError(t('game.shareUnavailable'));
            }}
            data-analytics-label="game-share"
          >
            <Icon name="share-01" size={14} /> {t('game.share')}
          </button>
          {isHost ? (
            <>
              <button
                className="btn btn-sm detailActions-primary"
                onClick={() => setShowInvite(true)}
                data-analytics-label="game-invite"
              >
                <Icon name="mail-01" size={14} /> {t('game.invitePlayers')}
              </button>
              <button
                className="btn btn-sm btn-ghost detailActions-secondary"
                onClick={() => setShowEditGame(true)}
                data-analytics-label="game-edit"
              >
                <Icon name="edit-01" size={14} /> {t('game.edit')}
              </button>
              {g.isPaid && (
                <button
                  className="btn btn-sm btn-ghost detailActions-secondary"
                  onClick={() => setShowPayments(true)}
                  data-analytics-label="game-payments"
                >
                  <Icon name="wallet-01" size={14} /> {t('game.managePayments')}
                </button>
              )}
              <button
                className="btn btn-sm btn-ghost detailActions-secondary"
                onClick={async () => {
                  if (await confirmDialog(t('game.finishConfirm'))) finishMut.mutate();
                }}
                disabled={finishMut.isLoading}
                data-analytics-label="game-finish"
              >
                <Icon name="checkmark-square-01" size={14} /> {t('game.finish')}
              </button>
              <button
                className="btn btn-sm btn-ghost detailActions-danger"
                onClick={async () => {
                  if (await confirmDialog(t('game.cancelConfirm'))) cancelMut.mutate();
                }}
                disabled={cancelMut.isLoading}
                data-analytics-label="game-cancel"
              >
                <Icon name="cancel-01" size={14} /> {t('game.cancel')}
              </button>
            </>
          ) : isJoined ? (
            <>
              <button
                className="btn btn-sm btn-ghost detailActions-secondary"
                onClick={() => leaveMut.mutate()}
                disabled={leaveMut.isLoading}
                data-analytics-label="game-leave"
              >
                <Icon name="logout-01" size={14} /> {t('game.leave')}
              </button>
            </>
          ) : g.isClosed && !myJoinRequest && g.status === 'OPEN' ? (
            <button
              className="btn btn-sm detailActions-primary detailActions-full"
              onClick={handleJoinClick}
              disabled={joinMut.isLoading}
              data-analytics-label="game-request-join"
            >
              <Icon name="mail-01" size={14} /> {t('game.requestToJoin')}
            </button>
          ) : myJoinRequest ? (
            <button className="btn btn-sm detailActions-primary detailActions-full" disabled>
              <Icon name="clock-01" size={14} /> {t('game.requestPending')}
            </button>
          ) : isFull ? (
            <button className="btn btn-sm detailActions-primary detailActions-full" disabled>
              <Icon name="user-group" size={14} /> {t('game.spotsFull')}
            </button>
          ) : (
            <button
              className="btn btn-sm detailActions-primary detailActions-full"
              onClick={handleJoinClick}
              disabled={joinMut.isLoading}
              data-analytics-label="game-join"
            >
              {t('game.join')}
            </button>
          )}
        </div>
      )}

      {/* Evaluate — every participant of a finished game, not only the
          host, and independent of whether the lobby was invite-only
          (`isClosed`). Hidden once they skip/submit (local markEvalDone). */}
      {isJoined && canEvaluate && id && !isEvalDone(id) && (
        <button
          className="btn btn-sm detailActions-primary detailActions-full"
          style={{ marginTop: 10 }}
          onClick={() => setShowEvaluate(true)}
        >
          <Icon name="award-01" size={14} /> {t('game.evaluations')}
        </button>
      )}

      {(joinMut.isError || leaveMut.isError || cancelMut.isError || shareError) && (
        <div className="error">
          {shareError ??
            (joinMut.error as Error)?.message ??
            (leaveMut.error as Error)?.message ??
            (cancelMut.error as Error)?.message}
        </div>
      )}

      {editBanner && (
        <div className="detailBanner" role="status">
          {editBanner}
        </div>
      )}

      <button className="btn secondary" style={{ marginTop: 12 }} onClick={() => navigate(-1)}>
        <Icon name="arrow-left-01" size={16} /> {t('common.close')}
      </button>

      {/* Modals */}
      <Modal open={pendingJoinBlockedWarn !== null} onClose={() => setPendingJoinBlockedWarn(null)} title={t('blacklist.warning.title')}>
        <p style={{ color: 'var(--text-secondary)' }}>
          {t('blacklist.warning.body', { name: (pendingJoinBlockedWarn ?? []).join(', ') })}
        </p>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={() => setPendingJoinBlockedWarn(null)}>
            {t('blacklist.warning.cancel')}
          </button>
          <button
            className="btn danger"
            onClick={() => {
              setPendingJoinBlockedWarn(null);
              joinMut.mutate();
            }}
          >
            {t('blacklist.warning.confirm')}
          </button>
        </div>
      </Modal>

      <ReportUserModal
        open={!!reportTarget}
        target={reportTarget}
        gameId={g.id}
        onClose={() => setReportTarget(null)}
      />

      <EvaluatePlayersModal
        open={showEvaluate}
        gameId={g.id}
        onClose={() => {
          markEvalDone(g.id);
          setShowEvaluate(false);
          qc.invalidateQueries(['game', id]);
          qc.invalidateQueries(['evaluations', 'pending']);
        }}
      />

      <InvitePlayerModal
        open={showInvite}
        gameId={g.id}
        onClose={() => setShowInvite(false)}
      />

      <EditGameModal
        open={showEditGame}
        game={g}
        onClose={() => setShowEditGame(false)}
        onSaved={(message) => {
          setEditBanner(message);
          window.setTimeout(() => setEditBanner(null), 4500);
        }}
      />

      <PaymentsModal
        open={showPayments}
        gameId={g.id}
        onClose={() => {
          setShowPayments(false);
          qc.invalidateQueries(['game', id]);
        }}
      />
    </>
  );
}

function PaymentsModal({ open, gameId, onClose }: { open: boolean; gameId: string; onClose: () => void }) {
  const api = useApi();
  const { t } = useI18n();
  const qc = useQueryClient();
  const listKey = ['payments', 'game', gameId] as const;
  const listQ = useQuery(listKey, () => api.listGamePayments(gameId), { enabled: open });
  /** Blocks double-tap (touch+click) from selecting then immediately clearing. */
  const inFlightUsers = useRef(new Set<string>());
  const lastSelectAt = useRef(new Map<string, number>());
  const [, bumpBusy] = useState(0);

  const setMethodMut = useMutation(
    ({ userId, method }: { userId: string; method: 'CASH' | 'BANK' | null }) =>
      api.setGamePayment(gameId, { userId, method }),
    {
      // Optimistic: only one method at a time; tap again clears → unpaid.
      onMutate: async ({ userId, method }) => {
        await qc.cancelQueries(listKey);
        const prev = qc.getQueryData<Awaited<ReturnType<typeof api.listGamePayments>>>(listKey);
        if (prev) {
          qc.setQueryData(listKey, {
            ...prev,
            participants: prev.participants.map((p) =>
              p.userId === userId
                ? {
                    ...p,
                    method,
                    isPaid: method != null,
                    paidAt: method ? new Date().toISOString() : null,
                  }
                : p,
            ),
          });
        }
        return { prev };
      },
      onError: (_err, _vars, ctx) => {
        if (ctx?.prev) qc.setQueryData(listKey, ctx.prev);
      },
      onSettled: (_data, _err, vars) => {
        inFlightUsers.current.delete(vars.userId);
        bumpBusy((n) => n + 1);
        void qc.invalidateQueries(listKey);
      },
    },
  );

  const participants = listQ.data?.participants ?? [];
  const paidCount = participants.filter((p) => p.isPaid).length;
  const cashCount = participants.filter((p) => p.method === 'CASH').length;
  const bankCount = participants.filter((p) => p.method === 'BANK').length;

  /**
   * Exclusive pick: Cash XOR Bank. Tap the active one again → unpaid.
   * Ignores re-entry while that row's request is in flight, and ignores
   * accidental clear within 500ms of a select (mobile double-fire).
   */
  const toggleMethod = (userId: string, current: 'CASH' | 'BANK' | null, next: 'CASH' | 'BANK') => {
    if (inFlightUsers.current.has(userId)) return;

    const clearing = current === next;
    if (clearing) {
      const selectedAt = lastSelectAt.current.get(userId) ?? 0;
      if (Date.now() - selectedAt < 500) return;
    }

    const method: 'CASH' | 'BANK' | null = clearing ? null : next;
    if (method) lastSelectAt.current.set(userId, Date.now());

    inFlightUsers.current.add(userId);
    bumpBusy((n) => n + 1);
    setMethodMut.mutate({ userId, method });
  };

  return (
    <Modal open={open} onClose={onClose} title={t('payments.title')}>
      {listQ.isLoading && <div className="skeleton" style={{ height: 80, borderRadius: 10 }} />}
      {listQ.data && (
        <>
          {/* Summary card: total + per-player share. Both are minor-unit
              integers in the API response, so /100 to render as decimal. */}
          <div className="paymentsSummary">
            <div className="paymentsSummary-row">
              <span className="paymentsSummary-label">{t('payments.totalCost')}</span>
              <strong className="paymentsSummary-value">
                {CURRENCY_SYMBOLS[listQ.data.currency as keyof typeof CURRENCY_SYMBOLS] ?? listQ.data.currency}
                {(listQ.data.totalCost / 100).toFixed(2)}
              </strong>
            </div>
            <div className="paymentsSummary-row paymentsSummary-row-accent">
              <span className="paymentsSummary-label">{t('payments.perPlayer')}</span>
              <strong className="paymentsSummary-value">
                {CURRENCY_SYMBOLS[listQ.data.currency as keyof typeof CURRENCY_SYMBOLS] ?? listQ.data.currency}
                {(listQ.data.perPlayer / 100).toFixed(2)}
              </strong>
            </div>
            <div className="paymentsSummary-row">
              <span className="paymentsSummary-label">{t('payments.collected')}</span>
              <strong className="paymentsSummary-value">
                {paidCount}/{participants.length}
                {paidCount > 0 ? (
                  <span className="paymentsSummary-split">
                    {' '}
                    ({cashCount} {t('payments.paidCash')} · {bankCount} {t('payments.paidBank')})
                  </span>
                ) : null}
              </strong>
            </div>
          </div>

          <p className="paymentsHint">{t('payments.methodHint')}</p>

          <div className="paymentsList">
            <div className="paymentsList-header">
              <span>{t('payments.players')}</span>
              <span className="paymentsList-headerMethods" aria-hidden="true">
                <span className="paymentsList-headerMethod" title={t('payments.paidCash')}>
                  <Icon name="dollar-01" size={14} />
                </span>
                <span className="paymentsList-headerMethod" title={t('payments.paidBank')}>
                  <Icon name="credit-card" size={14} />
                </span>
              </span>
            </div>
            {participants.map((p) => {
              const method = p.method === 'CASH' || p.method === 'BANK' ? p.method : null;
              const busy = inFlightUsers.current.has(p.userId);
              return (
                <div
                  key={p.userId}
                  className={`paymentsList-row${method ? ' isPaid' : ' isUnpaid'}`}
                >
                  <Photo
                    src={p.user.photoUrl}
                    name={p.user.firstName}
                    size={32}
                    variant="rounded"
                    topLeftBadge={
                      isAdminUser(p.user) ? (
                        <AdminCrownBadge title={t('profile.status.admin')} size="sm" />
                      ) : null
                    }
                  />
                  <span className="paymentsList-name">
                    {p.user.firstName}
                    {p.user.lastName ? ` ${p.user.lastName}` : ''}
                  </span>
                  <div
                    className="paymentsList-methods"
                    role="radiogroup"
                    aria-label={t('payments.status')}
                  >
                    <button
                      type="button"
                      role="radio"
                      className={`btn-icon paymentsMethodBtn paymentsMethodBtn-cash${
                        method === 'CASH' ? ' isOn' : ''
                      }`}
                      onClick={() => toggleMethod(p.userId, method, 'CASH')}
                      disabled={busy}
                      aria-checked={method === 'CASH'}
                      aria-label={
                        method === 'CASH' ? t('payments.markUnpaid') : t('payments.markPaidCash')
                      }
                      data-analytics-label={
                        method === 'CASH' ? 'payments-clear-cash' : 'payments-mark-cash'
                      }
                    >
                      <Icon name="dollar-01" size={16} />
                    </button>
                    <button
                      type="button"
                      role="radio"
                      className={`btn-icon paymentsMethodBtn paymentsMethodBtn-bank${
                        method === 'BANK' ? ' isOn' : ''
                      }`}
                      onClick={() => toggleMethod(p.userId, method, 'BANK')}
                      disabled={busy}
                      aria-checked={method === 'BANK'}
                      aria-label={
                        method === 'BANK' ? t('payments.markUnpaid') : t('payments.markPaidBank')
                      }
                      data-analytics-label={
                        method === 'BANK' ? 'payments-clear-bank' : 'payments-mark-bank'
                      }
                    >
                      <Icon name="credit-card" size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}
